import styled, { css } from 'styled-components'

import { Skeleton as Skeleton_, Star2Icon } from '@blade/ui-kit'

import type { ClassNameProps } from '@root/shared/react'

type ExploreTableSkeletonProps = {
  disabled: boolean
} & ClassNameProps

export const ExploreTableSkeleton: React.FC<ExploreTableSkeletonProps> = ({
  disabled = true,
  className,
}) => (
  <Root aria-disabled={disabled} className={className}>
    <Currency>
      <Col>
        <Star2Icon size={26} color="var(--background-4)" />
      </Col>
      <Icon
        css={css`
          margin-right: 10px;
        `}
      >
        <SkeletonDisabled height={42} width={42} variant="circular" />
        <SkeletonDisabled
          height={15}
          width={15}
          variant="circular"
          css={css`
            position: absolute;
            bottom: 2px;
            right: 0;
            background: var(--background-4);
            border-radius: 50%;
          `}
        />
      </Icon>
      <Group>
        <Row
          css={css`
            width: 80%;
            grid-template-columns: 1fr 1fr 1.5fr 0.3fr 0.3fr 0.3fr;
          `}
        >
          <Skeleton height={14} />
          <Skeleton height={14} />
          <Skeleton height={14} />
          <Skeleton height={14} />
          <Skeleton height={14} />
          <Skeleton height={14} />
        </Row>
        <Row
          css={css`
            width: 60%;
            grid-template-columns: 1fr 1fr 0.4fr;
          `}
        >
          <Skeleton height={12} />
          <Skeleton height={12} />
          <Skeleton height={12} />
        </Row>
      </Group>
    </Currency>
    <Col>
      <Skeleton height={14} width="80%" />
    </Col>
    <Col>
      <Skeleton height={14} width="80%" />
    </Col>
    <Col>
      <Skeleton height={14} width={14} />
    </Col>
    <Col>
      <Skeleton height={14} width={14} />
    </Col>
    <Col>
      <Skeleton height={14} width={14} />
    </Col>
    <Col>
      <Row
        css={css`
          width: 60%;
          grid-template-rows: auto auto;
          align-content: center;
          gap: 6px;
        `}
      >
        <Skeleton height={12} width="70%" />
        <Skeleton height={12} />
      </Row>
    </Col>
    <Col>
      <Skeleton height={14} width={14} />
    </Col>
    <Col
      css={css`
        width: 100%;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        gap: 4px;
      `}
    >
      <Skeleton height={14} width={14} />
      <Skeleton height={14} width={14} />
      <Skeleton height={14} width={14} />
      <Skeleton height={14} width={14} />
    </Col>
    <Col
      css={css`
        place-content: center;
      `}
    >
      <Skeleton height={30} width={10} />
    </Col>
  </Root>
)

const Root = styled.div`
  display: grid;
  grid-template-columns: 420px 100px 100px 90px 90px 90px 100px 90px 50px 1fr;
  height: 60px;

  place-content: center;
  padding-inline-end: 16px;
`

const Currency = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 6px;
  padding-inline-start: 20px;
`

const Group = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  align-items: center;
  align-content: center;
  gap: 6px;
`

const Col = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto;
`

const Icon = styled.div`
  border-radius: 50%;
  position: relative;
`

const SkeletonDisabled = styled(Skeleton_)`
  ${Root}[aria-disabled='true'] & {
    > div:after {
      animation: none;
    }
  }
`

const Skeleton = styled(SkeletonDisabled)`
  background: var(--backgroundA-1);
  > div {
    border-radius: 3px;
  }
`

const Row = styled.div`
  display: grid;
  grid-auto-flow: column;

  gap: 8px;
`
